import InfoPopup from "common/InfoPopup/InfoPopup";
import { useDispatch } from "react-redux";
import Spinner from "common/Spinner/Spinner";
import { useState, useEffect } from "react";
import ToggleSwitch from "common/Toggle/ToggleSwitch";
import InputField from "common/InputField/InputField";
import ProjectedIncome from "components/Dashboard/ProjectedIncome";
import { useCalculateLifetimeIncomeProjectionsMutation } from "service/APIService";
import Button from "common/Button/Button";
import sanitize from "utils/SanitizeHtml";
import ErrorMessage from "common/ErrorMessage/ErrorMessage";
import labels from "utils/LocalizedStrings";
import { amountFormatter, getAge } from "utils/Utilities";
import Modal from "react-bootstrap/Modal";

const IncomeProjectionCalculator = ({
  participantDetails,
  selectedTaxType,
  balances,
}) => {
  const [isJoint, setIsJoint] = useState(false);
  const [age, setAge] = useState("");
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [inputAge, setInputAge] = useState("");
  const [projectionData, setProjectionData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const handleOpen = () => setShowModal(true);
  const handleClose = () => setShowModal(false);
  const dispatch = useDispatch();
  const roundedAge = Math.round(participantDetails?.planMinPayoutAge);
  // Extract minimum payout age from participantDetails
  const minAge = participantDetails?.planMinPayoutAge ?? null;
  const participantAge = getAge(participantDetails.dob);

  const [calculateProjectionsMutation] =
    useCalculateLifetimeIncomeProjectionsMutation();

  useEffect(() => {
    // Whenever age or participantDetails change, validate
    validateAge(age);
  }, [age, participantDetails]);

  useEffect(() => {
    if (selectedTaxType) {
      setProjectionData(null);
      setAge("");
      setInputAge("");
      setIsJoint(false);
    }
  }, [selectedTaxType]);

  const validateAge = (val) => {
    const ageNum = Number(val);
    setErrors(null);
    setIsButtonDisabled(true);
    if (!val) {
      return;
    }
    if (isNaN(ageNum) || ageNum <= 0) {
      setErrors({
        errorMessage:
          labels?.dashboard?.lifetimeIncomeProjections?.validAgeErrorMsg,
      });
      return;
    }

    if (minAge && ageNum < minAge) {
      setErrors({
        errorMessage:
          labels?.dashboard?.lifetimeIncomeProjections?.minEligibleAgeErrorMsg.replace(
            "~minAge~",
            roundedAge
          ),
      });
      return;
    }

    if (participantAge && ageNum < participantAge) {
      setErrors({
        errorMessage:
          labels?.dashboard?.lifetimeIncomeProjections?.currentAgeErrorMsg.replace(
            "~participantAge~",
            participantAge
          ),
      });
      return;
    }

    if (ageNum > 99) {
      setErrors({
        errorMessage:
          labels?.dashboard?.lifetimeIncomeProjections?.maxAgeErrorMsg,
      });
      return;
    }
    setIsButtonDisabled(false);
  };

  const handleAgeChange = (val) => {
    setAge(parseInt(val));
    setInputAge(val);
  };


  const handleCalculate = async () => {
    const payload = {
      productSponsorId: participantDetails?.productSponsorId,
      productId: participantDetails?.productId,
      ownershipId: participantDetails?.ownershipId,
      electionChoice: isJoint ? "J" : "S",
      inputAge: inputAge,
      taxType: selectedTaxType,
    };
    setLoading(true);
    setErrors(null);
    calculateProjectionsMutation(payload)
      .unwrap()
      .then((response) => {
        dispatch(setProjectionData(response));
      })
      .catch((err) => {
        setErrors(err?.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="income-calculator card mt-3 shadow-sm">
      <div className="d-flex flex-column flex-md-row justify-content-between text-center mb-4">
        {/* Toggle */}
        {/* --- 1. Income Payment Type Toggle --- */}
        <div className="col-12 col-lg-4 col-md-6 col-sm-12 p-2 d-flex flex-column align-items-center">
          <div className="fw-bold mb-3 mt-3">
            {labels?.dashboard?.lifetimeIncomeProjections?.incomePaymentType}
            <InfoPopup
              position={"top"}
              content={
                labels?.dashboard?.lifetimeIncomeProjections
                  ?.incomePaymentTypePopover
              }
            />
          </div>
          <ToggleSwitch
            leftLabel={labels?.dashboard?.lifetimeIncomeProjections?.leftLabel}
            rightLabel={
              labels?.dashboard?.lifetimeIncomeProjections?.rightLabel
            }
            checked={isJoint}
            onChange={setIsJoint}
          />
        </div>
        <div className="border-div"></div>
        {/* Age input */}
        <div className="col-12 col-lg-4 col-md-6 col-sm-12 d-flex flex-column align-items-center">
          <div className="p-2">
            {labels?.dashboard?.lifetimeIncomeProjections?.projectionAgeInput}
          </div>
          <InputField
            type="number"
            id="inputAge"
            value={inputAge}
            wrapperClassName="col-md-2 col-12 w-25 mb-2"
            ariaLabel="Participant Input Age"
            placeholder=""
            disabled={!minAge}
            change={(val) => handleAgeChange(val)}
            errorMessage={errors}
            maxLength="3"
            inputClass="mb-1 text-center"
            onKeyDown={(e) => {
              if ([".", ",", "e", "E"].includes(e.key)) {
                e.preventDefault();
              }
            }}
          />

          <div
            className="small mb-3"
            dangerouslySetInnerHTML={sanitize(
              labels?.dashboard?.lifetimeIncomeProjections?.projectionAgeInputCondition.replace(
                "~minPlanPayoutAge~",
                roundedAge
              )
            )}
          ></div>
        </div>

        <div className="border-div"></div>
        {/* Rate */}

        <div className="col-12 col-md-4 mb-3 d-flex flex-column align-items-center">
          <div className="p-2 mb-2 text-center">
            {
              labels?.dashboard?.lifetimeIncomeProjections
                ?.projectionPercentageText
            }
          </div>
          <h3 className="text-center">
            {projectionData
              ? `${(Number(projectionData.withdrawalRate)*100).toFixed(2)}%`
              : "--"}
          </h3>
          <div className="learn-more">
            <a onClick={handleOpen}>Learn more</a>
          </div>
          <Modal
            show={showModal}
            size="lg"
            onHide={handleClose}
            dialogClassName="withdrawal-info-modal"
            backdrop="static"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {
                  labels?.dashboard?.lifetimeIncomeProjections
                    ?.projectionPercentageText
                }
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p
                className="mb-2"
                dangerouslySetInnerHTML={sanitize(
                  labels?.dashboard?.lifetimeIncomeProjections?.incomeProjectionsLearnMoreOverlayText
                    ?.replace("~date~", balances?.balancesAsOfDate)
                    ?.replace(
                      "~lifetimeIncomeValue~",
                      amountFormatter(balances?.lifeTimeIncomeAmount)
                    )
                )}
              ></p>
            </Modal.Body>
          </Modal>
        </div>
      </div>

      <div className=" d-flex mb-3 justify-content-center">
        <Button
          wrapperClassName="col-12 col-3 col-md-3 d-flex justify-content-center"
          type="primary"
          text={labels?.dashboard?.lifetimeIncomeProjections?.calculateButton}
          click={handleCalculate}
          disabled={isButtonDisabled}
          ariaLabel="Calculate"
        />
      </div>
      {errors && <ErrorMessage error={errors} wrapperClassName="mb-2" />}
      {/* Projected Income Section */}
      {loading && <Spinner />}
      {projectionData && (
        <ProjectedIncome projectionData={projectionData} age={inputAge} />
      )}
    </div>
  );
};

export default IncomeProjectionCalculator;
